import React, { useEffect, useRef, useState } from "react";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

const validateForm = (formData) => {
  const nextErrors = {};
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
  if (!formData.lastName.trim()) nextErrors.lastName = "Enter your last name.";

  if (!formData.email.trim()) {
    nextErrors.email = "Enter your email address.";
  } else if (!emailPattern.test(formData.email)) {
    nextErrors.email = "Enter a valid email address.";
  }

  if (!formData.subject) nextErrors.subject = "Choose a subject.";

  if (!formData.message.trim()) {
    nextErrors.message = "Tell us how we can help.";
  } else if (formData.message.trim().length < 10) {
    nextErrors.message = "Please add a little more detail.";
  }

  return nextErrors;
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const submitTimer = useRef(null);
  const contact = siteConfig.contact;

  useEffect(
    () => () => {
      if (submitTimer.current) window.clearTimeout(submitTimer.current);
    },
    []
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setShowSuccess(false);

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm(formData);

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      document.getElementById(`contact-${Object.keys(nextErrors)[0]}`)?.focus();
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    submitTimer.current = window.setTimeout(() => {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setShowSuccess(true);
    }, 650);
  };

  const fieldErrorProps = (field) => ({
    "aria-invalid": Boolean(errors[field]),
    "aria-describedby": errors[field] ? `contact-${field}-error` : undefined,
  });

  return (
    <main className="contact" aria-labelledby="contact-title">
      <section className="contact__intro">
        <p className="contact__eyebrow">Customer service</p>
        <h1 id="contact-title">Talk to us.</h1>
        <p>
          Questions about fit, fabric, an existing order or caring for your
          denim? Our team knows the collection and is ready to help.
        </p>
      </section>

      <div className="contact__layout">
        <aside className="contact__details" aria-label="Contact information">
          <div>
            <p className="contact__detail-label">Email</p>
            <a href={`mailto:${contact.email}`}>{contact.email}</a>
          </div>
          <div>
            <p className="contact__detail-label">Telephone</p>
            <a href={`tel:${contact.phone.replace(/[^\d+]/g, "")}`}>
              {contact.phone}
            </a>
          </div>
          <div>
            <p className="contact__detail-label">Visit or write</p>
            <address>
              {contact.address.map((line) => (
                <React.Fragment key={line}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </address>
          </div>
          <div>
            <p className="contact__detail-label">Customer care hours</p>
            <p>{contact.hours}</p>
          </div>
          <p className="contact__response-note">
            We aim to answer every message within two working days.
          </p>
        </aside>

        <section className="contact__form-panel" aria-labelledby="contact-form-title">
          <div className="contact__form-heading">
            <p>Send a message</p>
            <h2 id="contact-form-title">How can we help?</h2>
          </div>

          {showSuccess && (
            <div className="contact__success" role="status">
              <strong>Message received.</strong>
              <span>Thank you. Our team will be in touch shortly.</span>
            </div>
          )}

          <form className="contact__form" onSubmit={handleSubmit} noValidate>
            <div className="contact__field-row">
              <div className="contact__field">
                <label htmlFor="contact-firstName">First name</label>
                <input
                  id="contact-firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  {...fieldErrorProps("firstName")}
                />
                {errors.firstName && (
                  <span id="contact-firstName-error" className="contact__error">
                    {errors.firstName}
                  </span>
                )}
              </div>
              <div className="contact__field">
                <label htmlFor="contact-lastName">Last name</label>
                <input
                  id="contact-lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  {...fieldErrorProps("lastName")}
                />
                {errors.lastName && (
                  <span id="contact-lastName-error" className="contact__error">
                    {errors.lastName}
                  </span>
                )}
              </div>
            </div>

            <div className="contact__field-row">
              <div className="contact__field">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  {...fieldErrorProps("email")}
                />
                {errors.email && (
                  <span id="contact-email-error" className="contact__error">
                    {errors.email}
                  </span>
                )}
              </div>
              <div className="contact__field">
                <label htmlFor="contact-phone">Telephone (optional)</label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className="contact__field">
              <label htmlFor="contact-subject">What is this about?</label>
              <select
                id="contact-subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                {...fieldErrorProps("subject")}
              >
                <option value="">Choose a subject</option>
                <option value="product">Product and fit advice</option>
                <option value="order">An existing order</option>
                <option value="delivery">Delivery or returns</option>
                <option value="care">Repairs and garment care</option>
                <option value="other">Something else</option>
              </select>
              {errors.subject && (
                <span id="contact-subject-error" className="contact__error">
                  {errors.subject}
                </span>
              )}
            </div>

            <div className="contact__field">
              <label htmlFor="contact-message">Message</label>
              <textarea
                id="contact-message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                {...fieldErrorProps("message")}
              />
              {errors.message && (
                <span id="contact-message-error" className="contact__error">
                  {errors.message}
                </span>
              )}
            </div>

            <button className="contact__submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}

export default Contact;
